import { useState, useEffect, useCallback } from "react";

interface PasswordValidationResult {
  passwordStrong: boolean;
  passwordsMatch: boolean;
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  validationErrors: string[];
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  togglePasswordVisibility: () => void;
  toggleConfirmPasswordVisibility: () => void;
}

const MIN_PASSWORD_LENGTH = 8;
const PASSWORD_REGEX = {
  lower: /[a-z]/,
  upper: /[A-Z]/,
  number: /\d/,
  special: /[!@#$%^&*(),.?":{}|<>]/,
};

export const usePasswordValidation = (): PasswordValidationResult => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordStrong, setPasswordStrong] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validatePassword = useCallback((pass: string) => {
    const errors: string[] = [];
    
    if (pass.length < MIN_PASSWORD_LENGTH) {
      errors.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
    }
    if (!PASSWORD_REGEX.lower.test(pass)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    if (!PASSWORD_REGEX.upper.test(pass)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    if (!PASSWORD_REGEX.number.test(pass)) {
      errors.push("Password must contain at least one number");
    }
    if (!PASSWORD_REGEX.special.test(pass)) {
      errors.push("Password must contain at least one special character");
    }
    
    setValidationErrors(errors);
    setPasswordStrong(errors.length === 0);
  }, []);

  useEffect(() => {
    if (password) {
      validatePassword(password);
    } else {
      setValidationErrors([]);
      setPasswordStrong(false);
    }
  }, [password, validatePassword]);

  useEffect(() => {
    if (confirmPassword) {
      setPasswordsMatch(password === confirmPassword);
    } else {
      setPasswordsMatch(true);
    }
  }, [password, confirmPassword]);

  const togglePasswordVisibility = useCallback(() => setShowPassword(prev => !prev), []);
  const toggleConfirmPasswordVisibility = useCallback(() => setShowConfirmPassword(prev => !prev), []);

  return {
    passwordStrong,
    passwordsMatch,
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    validationErrors,
    setPassword,
    setConfirmPassword,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
  };
};

